import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useSortable } from '@dnd-kit/sortable'

import { createQuestion, updateQuestion, deleteQuestion } from '../../actions/certificationActions'
import { questionErrors, answersAttributes, showGenericError } from '../../utility/certificationBuilder'
import { useForm } from './useForm'
import { sortableStyle } from './sortable'
import DragHandle from './DragHandle'
import AnswerRow from './AnswerRow'
import DeleteConfirmationModal from './DeleteConfirmationModal'
import Input from '../common/form/Input'
import FieldError from '../common/FieldError'

import classes from './QuestionCard.module.scss'

const NEW_ANSWERS_COUNT = 4

const emptyAnswer = () => ({ key: Math.random(), text: '', correct: false })

const questionForm = (question) => ({
  prompt: question?.prompt || '',
  explanation: question?.explanation || '',
  answers: question
    ? question.answers.map(answer => ({ key: answer.id, ...answer }))
    : Array.from({ length: NEW_ANSWERS_COUNT }, emptyAnswer),
})

const QuestionCard = ({ quizId, question, number, open, onToggle, onClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const sortable = useSortable({ id: question?.id || 'new', disabled: !question })
  const { form, setForm, errors, setErrors, setField } = useForm(() => questionForm(question))
  const [isSaving, setIsSaving] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const visibleAnswers = form.answers.filter(answer => !answer._destroy)

  const updateAnswer = (key, changes) => setField('answers', form.answers.map(answer => answer.key === key ? { ...answer, ...changes } : answer))
  const markCorrect = (key) => setField('answers', form.answers.map(answer => ({ ...answer, correct: answer.key === key })))
  const removeAnswer = (key) => setField('answers', form.answers
    .map(answer => answer.key === key ? { ...answer, _destroy: true } : answer)
    .filter(answer => answer.id || !answer._destroy))
  const addAnswer = () => setField('answers', [...form.answers, emptyAnswer()])

  const handleSave = (e) => {
    e.preventDefault()
    const validationErrors = questionErrors(form)
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return }

    const attributes = { prompt: form.prompt.trim(), explanation: form.explanation.trim(), answersAttributes: answersAttributes(form.answers) }
    const request = question ? updateQuestion({ quizId, id: question.id, ...attributes }) : createQuestion({ quizId, ...attributes })

    setIsSaving(true)
    dispatch(request).unwrap()
      .then(({ question: saved }) => {
        toast.success(t('certificationBuilder.question.saveSuccess'), { position: toast.POSITION.TOP_RIGHT })
        question ? setForm(questionForm(saved)) : onClose()
      })
      .catch(showGenericError)
      .finally(() => setIsSaving(false))
  }

  const handleDelete = () => {
    setIsDeleting(true)
    dispatch(deleteQuestion({ quizId, id: question.id })).unwrap().catch((errors) => {
      errors?.base ? toast.error(errors.base[0], { position: toast.POSITION.TOP_RIGHT }) : showGenericError()
      setIsDeleting(false)
    })
  }

  return (
    <div ref={sortable.setNodeRef} style={sortableStyle(sortable)} className={classNames(classes.card, { [classes.cardOpen]: open })}>
      <div className={classNames('d-flex align-items-center', classes.head, { [classes.headOpen]: open })}>
        {question && <DragHandle small listeners={sortable.listeners} attributes={sortable.attributes} />}
        <button
          className={classNames('d-flex align-items-center flex-grow-1 text-left', classes.headButton)}
          onClick={onToggle}
          disabled={!question}
        >
          <span className={classes.number}>{t('certificationBuilder.question.number', { number })}</span>
          <span className={classNames('flex-grow-1', classes.title, { [classes.titleMuted]: !question?.prompt })}>
            {question?.prompt || t('certificationBuilder.question.newQuestion')}
          </span>
          {question && <i className={open ? 'fa fa-chevron-up' : 'fa fa-chevron-down'} aria-hidden='true' />}
        </button>
      </div>

      {open && (
        <form onSubmit={handleSave} className={classNames('d-flex flex-column', classes.body)}>
          <Input
            id={`questionPrompt-${question?.id || 'new'}`}
            type='textarea'
            value={form.prompt}
            placeholder={t('certificationBuilder.question.promptPlaceholder')}
            onChange={(e) => setField('prompt', e.target.value)}
            error={errors.prompt && t(`certificationBuilder.question.errors.${errors.prompt}`)}
          />

          <span className={classes.answersLabel}>{t('certificationBuilder.question.answers')}</span>
          <div className='d-flex flex-column'>
            {visibleAnswers.map(answer => (
              <AnswerRow
                key={answer.key}
                answer={answer}
                onChange={(text) => updateAnswer(answer.key, { text })}
                onMarkCorrect={() => markCorrect(answer.key)}
                onRemove={visibleAnswers.length > 2 ? () => removeAnswer(answer.key) : undefined}
              />
            ))}
            {errors.answers && <FieldError message={t(`certificationBuilder.question.errors.${errors.answers}`)} />}
            <button type='button' className={classNames('align-self-start', classes.linkButton)} onClick={addAnswer}>
              <i className='fa fa-plus mr-1' aria-hidden='true' />
              {t('certificationBuilder.question.addAnswer')}
            </button>
          </div>

          <div>
            <label htmlFor={`questionExplanation-${question?.id || 'new'}`} className={classes.explanationLabel}>
              {t('certificationBuilder.question.explanation')}
            </label>
            <Input
              id={`questionExplanation-${question?.id || 'new'}`}
              type='text'
              value={form.explanation}
              placeholder={t('certificationBuilder.question.explanationPlaceholder')}
              onChange={(e) => setField('explanation', e.target.value)}
            />
          </div>

          <div className='d-flex align-items-center justify-content-between'>
            <button type='button' className={classes.textButton} onClick={question ? () => setShowDeleteModal(true) : onClose} disabled={isSaving}>
              <i className='fa fa-trash-o mr-1' aria-hidden='true' />
              {t(question ? 'certificationBuilder.question.delete' : 'certificationBuilder.question.discard')}
            </button>
            <button type='submit' className={classNames('btn btn-primary text-white', classes.saveButton)} disabled={isSaving}>
              {isSaving && <i className='fa fa-spinner fa-spin mr-2' aria-hidden='true' />}
              {t('certificationBuilder.question.save')}
            </button>
          </div>
        </form>
      )}

      {question && (
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={t('certificationBuilder.question.deleteTitle')}
          message={t('certificationBuilder.question.deleteMessage')}
          isLoading={isDeleting}
          onClose={() => setShowDeleteModal(false)}
          onProceed={handleDelete}
        />
      )}
    </div>
  )
}

QuestionCard.propTypes = {
  quizId: PropTypes.number.isRequired,
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    prompt: PropTypes.string.isRequired,
    explanation: PropTypes.string,
    answers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
      correct: PropTypes.bool.isRequired,
    })).isRequired,
  }),
  number: PropTypes.number.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func,
}

export default QuestionCard
